/**
 * Single source of truth for page headers and breadcrumbs.
 *
 * ROUTE TREE
 * Each node maps to a real route in PrivateRouter. Intermediate paths that are
 * not real routes (e.g. /customer, /project) must NOT have a `to` so the
 * breadcrumb renders them as plain text instead of a broken link.
 *
 * Shape per node:
 *   label       — breadcrumb text
 *   title       — <h5> page title (falls back to label when omitted)
 *   to          — navigation target; omit when path is not a real route
 *   dynamic     — true when the segment is a :param placeholder
 *   getDynLabel — (options) => string  used when the segment is dynamic
 */
const ROUTE_TREE = {
    '/home': {
        label: 'Inicio',
        title: 'Dashboard',
        to: '/home',
        children: {
            '/dashboard': {
                label: 'Dashboard',
                children: {
                    '/dashboard/tickets': {
                        label: 'Tickets',
                        title: 'Dashboard de Tickets',
                        to: '/dashboard/tickets',
                    },
                },
            },

            // ── Customers ──────────────────────────────────────────────────
            '/customer': {
                label: 'Clientes',
                children: {
                    '/customer/list': {
                        label: 'Lista',
                        title: 'Clientes',
                        to: '/customer/list',
                    },
                    '/customer/create': {
                        label: 'Nuevo',
                        title: 'Nuevo Cliente',
                        to: '/customer/create',
                    },
                    '/customer/detail': {
                        label: 'Detalle',
                        title: 'Detalle del Cliente',
                        to: '/customer/list',         // list is the closest real parent
                        children: {
                            ':id': {
                                dynamic: true,
                                getDynLabel: (opts) => opts.customerDetailLabel || 'Detalle',
                                getDynTitle: (opts) => opts.customerDetailLabel || 'Detalle del Cliente',
                            },
                        },
                    },
                },
            },

            // ── Projects ───────────────────────────────────────────────────
            '/project': {
                label: 'Proyectos',
                children: {
                    '/project/list': {
                        label: 'Lista',
                        title: 'Proyectos',
                        to: '/project/list',
                    },
                    '/project/create': {
                        label: 'Nuevo',
                        title: 'Nuevo Proyecto',
                        to: '/project/create',
                    },
                    '/project/detail': {
                        label: 'Detalle',
                        title: 'Detalle del Proyecto',
                        to: '/project/list',
                        children: {
                            ':id': {
                                dynamic: true,
                                getDynLabel: (opts) => opts.projectDetailLabel || 'Detalle',
                                getDynTitle: (opts) => opts.projectDetailLabel || 'Detalle del Proyecto',
                            },
                        },
                    },
                },
            },

            // ── Groups ─────────────────────────────────────────────────────
            '/group': {
                label: 'Grupos',
                children: {
                    '/group/list': {
                        label: 'Lista',
                        title: 'Grupos',
                        to: '/group/list',
                    },
                    '/group/create': {
                        label: 'Nuevo',
                        title: 'Nuevo Grupo',
                        to: '/group/create',
                    },
                    '/group/detail': {
                        label: 'Detalle',
                        title: 'Detalle del Grupo',
                        to: '/group/list',
                        children: {
                            ':id': {
                                dynamic: true,
                                getDynLabel: (opts) => opts.groupDetailLabel || 'Detalle',
                                getDynTitle: (opts) => opts.groupDetailLabel || 'Detalle del Grupo',
                            },
                        },
                    },
                },
            },

            // ── Tasks / Tickets ────────────────────────────────────────────
            '/tasks': {
                label: 'Tickets',
                children: {
                    '/tasks/list': {
                        label: 'Lista',
                        title: 'Tickets',
                        to: '/tasks/list',
                    },
                    '/tasks/create': {
                        label: 'Nuevo',
                        title: 'Nuevo Ticket',
                        to: '/tasks/create',
                    },
                    '/tasks/detail': {
                        label: 'Detalle',
                        title: 'Detalle del Ticket',
                        to: '/tasks/list',
                        children: {
                            ':ticketId': {
                                dynamic: true,
                                getDynLabel: (opts) => opts.ticketDetailLabel || 'Detalle',
                                getDynTitle: (opts) => opts.ticketDetailLabel || 'Detalle del Ticket',
                            },
                        },
                    },
                },
            },

            // ── Notifications ──────────────────────────────────────────────
            '/notifications': {
                label: 'Notificaciones',
                title: 'Notificaciones',
                to: '/notifications',
            },

            // ── Support ────────────────────────────────────────────────────
            '/support': {
                label: 'Centro de Ayuda',
                title: 'Soporte',
                to: '/support',
                children: {
                    '/support/ticket': {
                        label: 'Ticket',
                        children: {
                            '/support/ticket/create': {
                                label: 'Crear ticket',
                                title: 'Crear Ticket de Soporte',
                                to: '/support/ticket/create',
                            },
                        },
                    },
                },
            },

            // ── Settings ───────────────────────────────────────────────────
            '/settings': {
                label: 'Settings',
                children: {
                    '/settings/records': {
                        label: 'Records',
                        title: 'Records',
                        to: '/settings/records',
                    },
                    '/settings/account': {
                        label: 'Cuenta',
                        title: 'Configuración de la Cuenta',
                        to: '/settings/account',
                    },
                    '/settings/profile': {
                        label: 'Mi Perfil',
                        title: 'Mi Perfil',
                        to: '/settings/profile',
                    },
                    '/settings/users': {
                        label: 'Usuarios',
                        title: 'Gestión de Usuarios',
                        to: '/settings/users',
                        children: {
                            '/settings/users/create': {
                                label: 'Crear',
                                title: 'Crear Usuario',
                                to: '/settings/users/create',
                            },
                            '/settings/users/edit': {
                                label: 'Editar',
                                title: 'Editar Usuario',
                                to: '/settings/users',
                                children: {
                                    ':userId': {
                                        dynamic: true,
                                        getDynLabel: (opts) => opts.userEditLabel || 'Editar',
                                        getDynTitle: (opts) => opts.userEditLabel || 'Editar Usuario',
                                    },
                                },
                            },
                        },
                    },
                    '/settings/audit-log': {
                        label: 'Auditoría',
                        title: 'Tenant Audit Log',
                        to: '/settings/audit-log',
                    },
                },
            },
        },
    },
};

// ─── Internal helpers ──────────────────────────────────────────────────────────

const MONGO_ID_RE = /^[a-f\d]{24}$/i;

/**
 * Walk the ROUTE_TREE following `segments` and collect breadcrumb items.
 * Returns { items, leafNode } where leafNode is the deepest matched config node.
 */
const walkTree = (segments, options = {}) => {
    const items = [{ label: 'Inicio', to: '/home' }];
    let currentChildren = ROUTE_TREE['/home'].children || {};
    let cumulativePath = '';
    let leafNode = ROUTE_TREE['/home'];

    for (let i = 0; i < segments.length; i++) {
        const segment = segments[i];
        cumulativePath += `/${segment}`;

        // Skip Mongo ObjectId segments — they become the label for the parent
        if (MONGO_ID_RE.test(segment)) {
            // Find the dynamic child node in currentChildren
            const dynNode = Object.values(currentChildren).find((n) => n.dynamic);
            if (dynNode) {
                leafNode = dynNode;
                items.push({
                    label: dynNode.getDynLabel(options),
                    to: dynNode.to,
                });
                currentChildren = dynNode.children || {};
            }
            continue;
        }

        const node = currentChildren[cumulativePath];

        if (!node) {
            // Unknown segment — render as-is without a link
            items.push({ label: segment });
            currentChildren = {};
            continue;
        }

        leafNode = node;

        items.push({
            label: node.label,
            to: node.to,
        });

        currentChildren = node.children || {};
    }

    return { items, leafNode };
};

// ─── Public API ────────────────────────────────────────────────────────────────

/**
 * Returns { title, section, subsection } for the given pathname.
 * `options` mirrors the second argument of getBreadcrumbItems.
 */
export const getHeaderByPath = (pathname = '/', options = {}) => {
    if (pathname === '/' || pathname === '/home') {
        return { title: 'Dashboard', section: 'Inicio', subsection: 'Dashboard' };
    }

    const segments = pathname.split('/').filter(Boolean);
    const { leafNode } = walkTree(segments, options);

    // Dynamic nodes expose getDynTitle
    const title = leafNode.getDynTitle
        ? leafNode.getDynTitle(options)
        : leafNode.title || leafNode.label || 'Bradev';

    return { title, section: 'Inicio', subsection: title };
};

/**
 * Returns an array of { label, to? } breadcrumb items for the given pathname.
 *
 * options:
 *   customerDetailLabel — name shown for /customer/detail/:id
 *   projectDetailLabel  — name shown for /project/detail/:id
 *   groupDetailLabel    — name shown for /group/detail/:id
 *   ticketDetailLabel   — code shown for /tasks/detail/:ticketId
 *   userEditLabel       — name shown for /settings/users/edit/:userId
 */
export const getBreadcrumbItems = (pathname = '/', options = {}) => {
    if (pathname === '/' || pathname === '/home') {
        return [{ label: 'Inicio', to: '/home' }];
    }

    const segments = pathname.split('/').filter(Boolean);
    const { items } = walkTree(segments, options);

    // Option C: the <h5> already shows where you are — breadcrumb is back-navigation only
    return items.slice(0, -1);
};